import axios, { type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import { NotificationService } from '../components/ui/NotificationService';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Per-request opt-in: a 403 on this request is an expected permission
     * outcome (optional data the role may not have). The interceptor skips the
     * global toast and `[API Error]` log for that 403 only; the error is still
     * rejected so the caller can fall back. Other statuses are unaffected.
     */
    expectForbidden?: boolean;
  }
}

/**
 * Returns true when the error is a request cancellation (AbortSignal or
 * axios.CancelToken). These must be silently re-thrown — they are NOT
 * network errors and should never trigger a toast or retry.
 */
function isCancelledRequest(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  const axErr = error as AxiosError | undefined;
  return axErr?.code === 'ERR_CANCELED' || axErr?.code === 'ECONNABORTED';
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

const TOKEN_KEY = 'epprem_token';
const REFRESH_TOKEN_KEY = 'epprem_refresh_token';
const USER_KEY = 'epprem_user';
const AUTH_TIMESTAMP_KEY = 'epprem_auth_timestamp';

function clearStoredAuthData() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(AUTH_TIMESTAMP_KEY);

  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(AUTH_TIMESTAMP_KEY);
}

function isAuthRequest(url?: string) {
  if (!url) {
    return false;
  }

  return [
    '/auth/login',
    '/auth/logout',
    '/auth/verify-mfa',
    '/auth/send-otp',
    '/auth/otp-login',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/validate-reset-token',
    '/auth/re-authenticate',
  ].some((path) => url.includes(path));
}

function isSilentNotFoundRequest(url?: string) {
  if (!url) {
    return false;
  }

  return url.includes('/page-instructions/engine');
}

// The can-create-assistance-case readiness check returns 403 to signal a
// normal "not eligible yet" state (e.g. patient not yet approved), not an
// access-control failure — callers read the reason from the response body.
function isSilentForbiddenRequest(url?: string) {
  if (!url) {
    return false;
  }

  return url.includes('/can-create-assistance-case');
}

function isPublicApprovalRequest(url?: string) {
  if (!url) {
    return false;
  }

  return url.includes('/patient-approvals');
}

// A failed call to the error-logging endpoint itself must never trigger
// another call to log that failure — without this guard, a transient outage
// on this one endpoint (e.g. the backend restarting mid-deploy) turns every
// other failing request during that window into a self-amplifying storm of
// "log the failure to log the failure" calls.
function isFrontendErrorLogRequest(url?: string) {
  if (!url) {
    return false;
  }

  return url.includes('/shared/log-frontend-error');
}

function redirectToLogin() {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const loginUrl = currentPath && currentPath !== '/login'
    ? `/login?returnUrl=${encodeURIComponent(currentPath)}`
    : '/login';

  window.location.replace(loginUrl);
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30s — prevent indefinitely hanging requests
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - add correlation ID and Authorization header
apiClient.interceptors.request.use(
  (config) => {
    const correlationId = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    config.headers['X-Correlation-ID'] = correlationId;
    
    // Add Authorization header if token exists
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Duplicate Error Toast Guard ─────────────────────────────────
// Prevents the same error message from appearing multiple times in quick
// succession (e.g. interceptor + component both catching the same failure).
let _lastErrorMessage = '';
let _lastErrorTimestamp = 0;
const ERROR_DEDUP_WINDOW_MS = 2000; // suppress identical errors within 2 s

function showDedupedError(message: string, correlationId?: string) {
  const now = Date.now();
  if (message === _lastErrorMessage && now - _lastErrorTimestamp < ERROR_DEDUP_WINDOW_MS) {
    return; // duplicate — suppress
  }
  _lastErrorMessage = message;
  _lastErrorTimestamp = now;
  NotificationService.error(message, correlationId);
}

// Response interceptor - handle errors globally
// SUCCESS: No auto-toast. Components show their own success feedback for
// mutations (create / update / delete). GET responses are always silent.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Cancelled requests (React Query AbortSignal, StrictMode unmounts)
    // must be silently re-thrown — they are NOT network errors.
    if (isCancelledRequest(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const correlationId = error.response?.headers['x-correlation-id']
      || error.response?.data?.correlationId;
    const requestUrl = error.config?.url as string | undefined;

    // 401 → redirect to login (skip for auth endpoints and public approval endpoints)
    if (status === 401 && !isAuthRequest(requestUrl) && !isPublicApprovalRequest(requestUrl)) {
      clearStoredAuthData();

      if (window.location.pathname !== '/login') {
        redirectToLogin();
      }
      // Don't toast for auth redirects — user will see login page
      return Promise.reject(error);
    }

    // Expected, caller-handled 403 (see AxiosRequestConfig.expectForbidden)
    if (status === 403 && error.config?.expectForbidden) {
      return Promise.reject(error);
    }

    // Derive a user-friendly message
    let message = 'Something went wrong. Please try again.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (status === 400) {
      message = 'Invalid request. Please check your input.';
    } else if (status === 403) {
      message = 'Access denied.';
    } else if (status === 404) {
      message = 'The requested resource was not found.';
    } else if (status === 409) {
      message = 'Conflict — this record may have been modified by another user.';
    } else if (status === 422) {
      message = 'Validation failed. Please review your input.';
    } else if (status && status >= 500) {
      message = 'Server error. Please try again later.';
    } else if (!error.response) {
      message = 'Network error. Please check your connection.';
    }

    // Treat instruction-engine 404s and readiness-check 403s as normal states.
    if (
      !(status === 404 && isSilentNotFoundRequest(requestUrl)) &&
      !(status === 403 && isSilentForbiddenRequest(requestUrl))
    ) {
      // Show error toast globally (with dedup to avoid double toasts when
      // a component also catches and displays the same error).
      showDedupedError(message, correlationId);
    }

    console.error('[API Error]', {
      status,
      message,
      correlationId,
      url: error.config?.url,
      method: error.config?.method,
    });

    // Log frontend errors to backend for 5xx (never for the logger's own
    // endpoint — see isFrontendErrorLogRequest)
    if (status >= 500 && !isFrontendErrorLogRequest(requestUrl)) {
      try {
        await logFrontendError({
          correlationId: correlationId || crypto.randomUUID(),
          moduleName: 'Frontend',
          apiOrScreenName: window.location.pathname,
          errorType: 'Integration',
          errorMessage: error.message,
          requestPayload: JSON.stringify({
            url: error.config?.url,
            method: error.config?.method,
          }),
        });
      } catch (logError) {
        console.error('Failed to log error:', logError);
      }
    }
    
    return Promise.reject(error);
  }
);

// ── Smart Retry Interceptor ────────────────────────────────────
// Retries only idempotent methods (GET/HEAD/OPTIONS) on transient
// failures (network errors or 5xx). Never retries mutations.
const MAX_RETRIES = 2;

apiClient.interceptors.response.use(undefined, async (error) => {
  // Never retry cancelled requests
  if (isCancelledRequest(error)) return Promise.reject(error);

  const config = error.config as InternalAxiosRequestConfig & { __retryCount?: number };
  if (!config) return Promise.reject(error);

  const method = (config.method ?? '').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
  const isRetryable = !error.response || (error.response.status >= 500);

  config.__retryCount = config.__retryCount ?? 0;

  if (!isIdempotent || !isRetryable || config.__retryCount >= MAX_RETRIES) {
    return Promise.reject(error);
  }

  config.__retryCount += 1;

  // Exponential back-off: 500ms, 1000ms
  await new Promise((resolve) => setTimeout(resolve, 500 * config.__retryCount!));

  return apiClient(config);
});

interface FrontendErrorLog {
  correlationId: string;
  moduleName: string;
  apiOrScreenName: string;
  errorType: string;
  errorMessage: string;
  requestPayload?: string;
}

async function logFrontendError(error: FrontendErrorLog) {
  try {
    await apiClient.post('/shared/log-frontend-error', error);
  } catch (err) {
    console.error('Error logging failed:', err);
  }
}

export default apiClient;
